import type { FC, PropsWithChildren } from 'react';
import { Children, useCallback, useState } from 'react';

import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import { cn } from '../../utils/cn';

import type { SliderControlsDisplayType } from '../../types';
import { embla } from './embla.css';

type Props = PropsWithChildren<{
  options?: EmblaOptionsType;
  isLive?: boolean;
  isCarousel?: boolean;
  className?: string;
  isDragDisabled?: boolean;
  controlsDisplayType?: SliderControlsDisplayType;
}>;

const Carousel: FC<Props> = ({ options, children, controlsDisplayType }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [canScroll, setCanScroll] = useState({
    canScrollPrev: false,
    canScrollNext: true,
  });
  const childCount = Children.count(children);
  const hasSingleItem = childCount <= 1;

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
    setCanScroll({
      canScrollPrev: !!emblaApi && !!emblaApi.canScrollPrev(),
      canScrollNext: !!emblaApi && !!emblaApi.canScrollNext(),
    });
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
    setCanScroll({
      canScrollPrev: !!emblaApi && !!emblaApi.canScrollPrev(),
      canScrollNext: !!emblaApi && !!emblaApi.canScrollNext(),
    });
  }, [emblaApi]);

  return (
    <div className={embla}>
      <div className={cn('embla', controlsDisplayType && `embla__controls__${controlsDisplayType}`)}>
        {!hasSingleItem && (
          <button
            className={cn('embla__prev', canScroll.canScrollPrev ? 'opacity-100' : 'opacity-50')}
            onClick={scrollPrev}
            type="button"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
        )}
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">{children}</div>
        </div>
        {!hasSingleItem && (
          <button
            className={cn('embla__next', canScroll.canScrollNext ? 'opacity-100' : 'opacity-50')}
            onClick={scrollNext}
            type="button"
          >
            <CaretRight size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
};

const EmblaCarousel = ({ children, options, isLive, isCarousel, controlsDisplayType }: Props) => {
  if (!isCarousel) {
    return <>{children}</>;
  }

  const isDragEnabled = isLive && isCarousel;

  return (
    <Carousel options={options} isDragDisabled={!isDragEnabled} controlsDisplayType={controlsDisplayType}>
      {children}
    </Carousel>
  );
};

export default EmblaCarousel;
